import {Injectable} from "@angular/core";
import {environment} from "../../../environments/environment";
import {
  createInitialRequestCriteria,
  RequestState,
  RequestStore
} from "./request.store";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  tap,
  timeout
} from "rxjs";
import {AuthService} from "@auth0/auth0-angular";
import {
  Request,
  RequestCriteria,
  RequestListItem,
  RequestStatus
} from "../../data-access/api-folders/Folder";
import {HttpClient, HttpParams} from "@angular/common/http";
import {arrayRemove, arrayUpsert} from "@datorama/akita";
import {PageResponse} from "../../data-access/PageResponse";
import {retryBackoff} from "../../data-access/retry.service";

@Injectable({ providedIn: 'root' })
export class RequestStoreService {
  private readonly baseUrl: string;
  private apiV1: string = environment.apiV1;
  private baseRef: string = environment.origin;
  private serviceCore: string = environment.service.core;

  /**
   * Source unique des critères de la liste. `ReplaySubject` et non `BehaviorSubject` : rien n'est
   * chargé avant que des critères réels soient poussés (cf. DECISIONS.md du 2026-10-01).
   */
  private readonly criteria$: ReplaySubject<RequestCriteria>
    = new ReplaySubject<RequestCriteria>(1);

  /** Le debounce ne porte que sur la frappe : changer de page ou de facette reste immédiat. */
  private readonly searchTerm$: Subject<string>
    = new Subject<string>();

  /** Les écouteurs vivent le temps de la session : ne jamais les installer deux fois. */
  private listening: boolean = false;

  constructor(
    private store: RequestStore,
    private httpClient: HttpClient,
    private authService: AuthService,
  ) {
    this.baseUrl = environment.local;
  }

  public setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  /**
   * Au démarrage, seuls les **statuts de référence** sont chargés : ils alimentent les facettes et
   * les libellés, et il y en a huit. Les demandes elles-mêmes ne sont plus téléchargées ici — le
   * navigateur enchaînait auparavant toutes les pages de 50 pour pouvoir filtrer et regrouper en
   * mémoire, ce qui rendait le démarrage interminable sur une connexion dégradée.
   */
  public init(): void {
    this.authService.isLoading$
      .pipe(
        // wait until SDK finished init/callback
        filter((loading: boolean) => !loading),
        switchMap(() => this.authService.isAuthenticated$),
        first(),
        // safe: token is ready, interceptor will attach it
        switchMap((isAuth: boolean) => {
          if (!isAuth) {
            this.authService.loginWithRedirect({ appState: { target: '/services'} });
            return EMPTY;
          }
          if (!this.listening) {
            this.listening = true;
            this.listenToCriteria();
            this.listenToSearchTerm();
          }
          this.loadRequestStatuses();
          // Akita démarre un store avec `loading: true`.
          this.setLoading(false);
          return EMPTY;
        }),
        catchError((err) => {
          console.error('[bootstrap requests] init failed', err);
          this.setLoading(false);
          return EMPTY;
        })
      ).subscribe();
  }

  private listenToCriteria(): void {
    this.criteria$
      .pipe(
        distinctUntilChanged((left: RequestCriteria, right: RequestCriteria) =>
          this.criteriaKey(left) === this.criteriaKey(right)),
        tap(() => this.setLoading(true)),
        switchMap((criteria: RequestCriteria) => this.fetchPage(criteria)
          .pipe(
            timeout({ first: 15000 }),
            retryBackoff(1, 400),
            tap((res: PageResponse<RequestListItem>) => {
              this.store.update((state: RequestState) => ({
                ...state,
                requests: res.items ? [ ...res.items ] : [],
                requestCriteria: { ...criteria },
                requestTotalItems: res.totalItems,
                requestTotalPage: res.totalPages,
                error: undefined
              }));
            }),
            catchError((error) => {
              this.store.setError(error);
              return of(null);
            })
          )),
        tap(() => this.setLoading(false))
      )
      .subscribe();
  }

  private listenToSearchTerm(): void {
    this.searchTerm$
      .pipe(
        debounceTime(350),
        distinctUntilChanged()
      )
      .subscribe((term: string) => this.patchCriteria({ term, page: 0 }));
  }

  /** Point d'entrée des onglets : critères propres à l'onglet puis première page. */
  public loadFirstPage(criteria: Partial<RequestCriteria>): void {
    this.criteria$.next({
      ...createInitialRequestCriteria(),
      ...criteria,
      page: 0
    });
  }

  /** Recherche serveur ; le terme est debouncé avant d'atteindre le réseau. */
  public search(term: string): void {
    this.searchTerm$.next(term ? term.trim() : '');
  }

  /**
   * Applique les facettes. Elles sont **serveur** : appliquées en mémoire, elles ne filtreraient
   * que les candidats de la page courante et donneraient un résultat faux.
   */
  public applyFilters(filters: Partial<RequestCriteria>): void {
    this.patchCriteria({ ...filters, page: 0 });
  }

  public goToPage(pageIndex: number): void {
    // nz-table indexe les pages à partir de 1, l'API à partir de 0.
    this.patchCriteria({ page: Math.max(0, pageIndex - 1) });
  }

  public changePageSize(size: number): void {
    this.patchCriteria({ size, page: 0 });
  }

  public reloadCurrentPage(): void {
    this.criteria$.next({ ...this.store.getValue().requestCriteria });
  }

  private patchCriteria(patch: Partial<RequestCriteria>): void {
    const current: RequestCriteria = this.store.getValue().requestCriteria;
    this.criteria$.next({ ...current, ...patch });
  }

  private fetchPage(criteria: RequestCriteria): Observable<PageResponse<RequestListItem>> {
    const url: string = `${this.baseRef}${this.serviceCore}${this.apiV1}requests/search`;

    let httpParams: HttpParams = new HttpParams()
      .set('page', String(criteria.page))
      .set('size', String(criteria.size));

    if (criteria.term) {
      httpParams = httpParams.set('q', criteria.term);
    }
    if (criteria.statusIndex !== null) {
      httpParams = httpParams.set('statusIndex', String(criteria.statusIndex));
    }

    if (criteria.unassigned) {
      httpParams = httpParams.set('unassigned', 'true');
    }
    if (criteria.dateFrom !== null && criteria.dateTo !== null) {
      httpParams = httpParams
        .set('dateFrom', String(criteria.dateFrom))
        .set('dateTo', String(criteria.dateTo));
    }
    // Les facettes multi-valeurs se répètent dans la query string (statusIds=a&statusIds=b).
    criteria.ownerIds.forEach((id: string) => httpParams = httpParams.append('ownerIds', id));
    criteria.statusIds.forEach((id: string) => httpParams = httpParams.append('statusIds', id));
    criteria.residenceIds.forEach((id: string) => httpParams = httpParams.append('residenceIds', id));
    criteria.stayTypes.forEach((t: string) => httpParams = httpParams.append('stayTypes', t));
    criteria.customerTypes.forEach((t: string) => httpParams = httpParams.append('customerTypes', t));

    return this.httpClient.get<PageResponse<RequestListItem>>(url, { params: httpParams });
  }

  private criteriaKey(criteria: RequestCriteria): string {
    return [
      criteria.term,
      criteria.statusIndex,
      [...criteria.statusIds].sort().join(','),
      [...criteria.ownerIds].sort().join(','),
      criteria.unassigned,
      [...criteria.residenceIds].sort().join(','),
      [...criteria.stayTypes].sort().join(','),
      [...criteria.customerTypes].sort().join(','),
      criteria.dateFrom,
      criteria.dateTo,
      criteria.page,
      criteria.size
    ].join('|');
  }

  private loadRequestStatuses(): void {
    this.fetchRequestStatuses()
      .pipe(
        timeout({ first: 10000 }),
        retryBackoff(1, 400),
        tap((statuses: RequestStatus[]) => {
          this.store.update((state: RequestState) => ({
            ...state,
            requestStatuses: statuses ? [ ...statuses ] : [],
            error: undefined
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Compteur du menu : nombre de demandes dont le statut est en dessous de l'index donné. Il était
   * calculé en mémoire, ce qui obligeait à avoir téléchargé toutes les demandes.
   */
  public loadPendingCount(statusIndex: number): void {
    this.httpClient.get<number>(
      `${this.baseRef}${this.serviceCore}${this.apiV1}requests/count`,
      { params: new HttpParams().set('statusIndex', String(statusIndex)) }
    )
      .pipe(
        timeout({ first: 10000 }),
        tap((count: number) => {
          this.store.update((state: RequestState) => ({
            ...state,
            requestPendingCount: count ?? 0
          }));
        }),
        catchError((error) => {
          console.error('[requests count] load failed', error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  private fetchRequestStatuses = () => this.httpClient.get<RequestStatus[]>(
    `${this.baseRef}${this.serviceCore}${this.apiV1}folder/statuses`
  );

  /**
   * UPSERT AND DELETE METHODS
   *
   *
   *
   */

  /**
   * Répercute une écriture sur la page courante. Le store ne détenant plus que les colonnes de la
   * liste, le `Request` complet renvoyé par l'API est projeté ici. Une création n'est pas insérée
   * de force : elle n'appartient peut-être ni à la page ni à l'onglet courants — utiliser
   * `reloadCurrentPage()` dans ce cas.
   */
  public upsertRequest(request: Request): void {
    if (!request || !request.id) {
      return;
    }

    this.store.update((state: RequestState) => {
      const isInCurrentPage: boolean = state.requests
        .some((item: RequestListItem) => item.id === request.id);

      return {
        ...state,
        requests: isInCurrentPage
          ? arrayUpsert(state.requests, request.id, this.toListItem(request))
          : state.requests
      };
    });
  }

  public async deleteRequest(requestId: string): Promise<boolean> {
    return new Promise<boolean>((resolve => {
      this.store.update((state: RequestState) => ({
        ...state,
        requests: arrayRemove(state.requests, requestId)
      }));
      resolve(true);
    }));
  }

  private toListItem(request: Request): RequestListItem {
    return {
      id: request.id,
      dateAdd: request.dateAdd,
      dateUpd: request.dateUpd,
      enabled: request.enabled,
      requestNumber: request.requestNumber,
      requestScoring: request.requestScoring,
      requestThirdParty: request.requestThirdParty,
      requestStartDate: request.requestStartDate,
      requestEndDate: request.requestEndDate,
      requestExpiryDate: request.requestExpiryDate,
      requestStayType: request.requestStayType,
      requestCustomer: {
        id: request.requestCustomer?.id ?? '',
        customerFullName: request.requestCustomer?.customerFullName ?? '',
        customerEmail: request.requestCustomer?.customerEmail ?? '',
        customerType: request.requestCustomer?.customerType ?? '',
        customerBirthdate: request.requestCustomer?.customerBirthdate ?? 0,
      },
      requestResidence: request.requestResidence
        ? {
          id: request.requestResidence.id,
          residenceDisplayName: request.requestResidence.residenceDisplayName,
          residenceColor: request.requestResidence.residenceColor,
        }
        : null,
      requestStatus: request.requestStatus
        ? {
          id: request.requestStatus.id,
          index: request.requestStatus.index,
          statusName: request.requestStatus.statusName,
          statusColor: request.requestStatus.statusColor,
          statusIcon: request.requestStatus.statusIcon,
        }
        : null,
      requestOwner: request.requestOwner
        ? {
          id: request.requestOwner.id ?? '',
          userFirstName: request.requestOwner.userFirstName ?? '',
          userLastName: request.requestOwner.userLastName ?? '',
        }
        : null,
      requestUnitType: request.requestUnitType
        ? {
          id: request.requestUnitType.id,
          unitTypeName: request.requestUnitType.unitTypeName,
        }
        : null,
    };
  }

}
